import React from "react";
import { motion } from "framer-motion";
import Icon from "../../../components/AppIcon";
import Footer from '../../home/components/Footer';

const ACCENT_COLOR_CLASS = "text-[#1e40af]";
const ACCENT_BORDER_CLASS = "border-[#0070f3]";
const ACCENT_BG_CLASS = "bg-[#1e40af]";
const LIGHT_BG_CLASS = "bg-gray-50";

const aboutData = {
    hero: {
        tag: "OUR JOURNEY",
        headline: "Crafting the Future, One Innovation at a Time.",
        subtext: "Founded on a passion for technology and a desire to solve complex problems, Innovate Inc. transforms business challenges into elegant, scalable digital solutions. We leverage innovation and creativity to build products that not only meet but exceed the expectations of our clients and shape the world for the better.",
    },
    mvp: [
        {
            title: "Our Mission",
            icon: "Target",
            description: "To empower businesses with innovative and reliable technology solutions that drive growth, efficiency, and success. We aim to foster long-term, mutually beneficial partnerships based on trust and mutual respect.",
        },
        {
            title: "Our Vision",
            icon: "Eye",
            description: "To be a global leader in digital transformation, recognized for our pioneering spirit, our commitment to quality, and our positive impact on the industry. We envision a future where technology is seamlessly integrated into every aspect of business.",
        },
    ],
    founders: {
        tag: "THE VISIONARIES",
        headline: "Meet Our Founders",
        subtext: "Two minds united by a shared vision to transform the future of technology.",
        list: [
            {
                name: "Fenil Khunt",
                title: "Managing Director",
                quote: "My passion for technology and leadership drives our mission to create innovative solutions that make a real difference in people's lives.",
                imageSrc: "/assets/images/employee.png"
            },
            {
                name: "Dixit Dhanani",
                title: "Managing Director",
                quote: "I believe that the best innovation comes from understanding the core problems and building elegant solutions that exceed expectations.",
                imageSrc: "/assets/images/employee.png"
            },
        ]
    },
    innovators: {
        tag: "THE INNOVATORS",
        headline: "The Minds Behind Our Success",
        subtext: "We are a collective of designers, developers, and strategists passionate about building what's next.",
        list: [
            { name: "John Smith", title: "Lead Developer", imageSrc: "/assets/images/story.jpg" },
            { name: "Emily White", title: "UX/UI Designer", imageSrc: "/assets/images/story.jpg" },
            { name: "Michael Chen", title: "Product Manager", imageSrc: "/assets/images/story.jpg" },
            { name: "Sarah Brown", title: "Marketing Specialist", imageSrc: "/assets/images/story.jpg" },
        ]
    },
    coreValues: {
        tag: "OUR CORE PRINCIPLES",
        headline: "What Drives Us",
        subtext: "Our values are the bedrock of our culture and guide every decision we make.",
        list: [
            {
                title: "Teamwork",
                icon: "Users",
                iconSize: 40,
                description: "We achieve more together. We foster a collaborative environment where every voice is heard, valued, and contributes to our shared success."
            },
            {
                title: "Creativity",
                icon: "Lightbulb",
                iconSize: 40,
                description: "Innovation is in our DNA. We encourage curiosity, experimentation, and constantly seek creative solutions to the most challenging problems."
            },
        ]
    }
};

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 },
    },
};

const itemFadeInUp = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            duration: 0.7,
            ease: [0.25, 0.46, 0.45, 0.94],
        },
    },
};
const headerAnimate = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" },
    },
};

// --- Updated Components for Animations ---

const FounderCard = ({ name, title, quote, imageSrc }) => (
    <motion.div
        className="text-center p-6 bg-white rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300"
        variants={itemFadeInUp}
        whileHover={{ scale: 1.03, rotate: 1.5, transition: { duration: 0.3 } }} // Zoom and Cross (Rotate)
    >
        <div className="mx-auto w-40 h-40 rounded-lg overflow-hidden mb-4 shadow-md">
            <img
                src={imageSrc}
                alt={name}
                className="w-full h-full object-cover"
                loading="lazy"
            />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{name}</h3>
        <p className={`${ACCENT_COLOR_CLASS} text-sm font-semibold mb-3`}>{title}</p>
        <p className="text-gray-600 text-sm italic pt-3 mt-3">
            "{quote}"
        </p>
    </motion.div>
);

const InnovatorCard = ({ name, title, imageSrc }) => (
    <motion.div
        className="text-center bg-white p-4 rounded-lg hover:shadow-md transition-all duration-300 cursor-pointer"
        variants={itemFadeInUp}
        whileHover={{ scale: 1.05, y: -2 }} // Zoom
    >
        <div className="mx-auto w-24 h-24 rounded-full overflow-hidden mb-3">
            <img
                src={imageSrc}
                alt={name}
                className="w-full h-full object-cover"
            />
        </div>
        <h3 className="text-lg font-bold text-gray-900 line-clamp-1">{name}</h3>
        <p className="text-gray-500 text-xs line-clamp-1">{title}</p>
    </motion.div>
);


const MvCard = ({ title, icon, description }) => (
    <motion.div
        className="bg-white p-8 rounded-xl text-left shadow-lg border-t-4 border-gray-100 transition-all h-full flex flex-col cursor-pointer"
        whileHover={{
            y: -5,
            scale: 1.01,
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.2)", // Zoom and Shadow change
            borderTopColor: ACCENT_COLOR_CLASS // Blink/Flash effect on border
        }}
        transition={{ duration: 0.3 }}
        variants={itemFadeInUp}
    >
        <div className="flex items-start mb-4">
            <Icon
                name={icon}
                size={40}
                className={`flex-shrink-0 mr-4 ${ACCENT_COLOR_CLASS}`}
            />
            <h3 className="text-3xl font-extrabold text-gray-900 leading-snug">
                {title}
            </h3>
        </div>
        <p className="text-gray-600 text-base flex-grow">{description}</p>
    </motion.div>
);


const ValueCard = ({ title, icon, iconSize = 32, description }) => (
    <motion.div
        className="bg-white p-6 rounded-xl text-left shadow-md transition-all h-full overflow-hidden relative cursor-pointer"
        whileHover={{
            scale: 1.02,
            boxShadow: "0 10px 20px rgba(0, 0, 0, 0.15)", // Zoom and Shadow change
            rotate: -1.5 // Cross (Rotate)
        }}
        variants={itemFadeInUp}
    >
        <div className={`absolute -bottom-8 -right-8 w-14 h-24 `}></div>

        <div className="flex items-start mb-3 relative z-10">
            <Icon
                name={icon}
                size={iconSize}
                className={`${ACCENT_COLOR_CLASS} flex-shrink-0 mr-4 bg-blue-100 p-2 rounded-full shadow-sm`}
            />
            <h3 className="text-xl font-bold text-gray-900 mt-1">{title}</h3>
        </div>
        <p className="text-gray-600 text-base mt-4 relative z-10">{description}</p>
    </motion.div>
);

const AboutPage = () => {
    const { hero, mvp, founders, innovators, coreValues } = aboutData;

    return (
        <div className="font-sans bg-white text-gray-900 min-h-screen">


            <section className="py-24 md:py-28 ">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <motion.p
                        className={`${ACCENT_COLOR_CLASS} text-base font-semibold uppercase tracking-widest mb-4`}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
                    >
                        {hero.tag}
                    </motion.p>

                    <motion.h1
                        className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight"
                        variants={headerAnimate} initial="hidden" animate="visible"
                    >
                        {hero.headline.split(',').map((part, index) => (
                            <React.Fragment key={index}>
                                {part}{index === 0 && ','} {index === 0 && <br />}
                            </React.Fragment>
                        ))}
                    </motion.h1>

                    <motion.p
                        className="text-lg text-gray-600 max-w-3xl mx-auto mt-8 font-light"
                        variants={headerAnimate} initial="hidden" animate="visible" transition={{ delay: 0.2 }}
                    >
                        {hero.subtext}
                    </motion.p>
                </div>
            </section>

            <section className="py-4 bg-gray-50">
                <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 text-left">

                    <motion.div
                        className="p-4"
                        variants={itemFadeInUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                    >
                        <div className="flex items-center mb-4 pb-4">
                            <div className={`flex-shrink-0 mr-4 p-3 rounded-full ${ACCENT_BG_CLASS} bg-opacity-10`}>
                                <Icon
                                    name={mvp[0].icon}
                                    size={36}
                                    className={`${ACCENT_COLOR_CLASS}`}
                                />
                            </div>
                            <h2 className={`text-3xl font-extrabold text-gray-900`}>
                                {mvp[0].title}
                            </h2>
                        </div>
                        <p className="text-lg text-gray-700 pt-0">
                            {mvp[0].description}
                        </p>
                    </motion.div>

                    <motion.div
                        className="p-4"
                        variants={itemFadeInUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ delay: 0.1 }}
                    >
                        <div className="flex items-center mb-4 pb-4">
                            <div className={`flex-shrink-0 mr-4 p-3 rounded-full ${ACCENT_BG_CLASS} bg-opacity-10`}>
                                <Icon
                                    name={mvp[1].icon}
                                    size={36}
                                    className={`${ACCENT_COLOR_CLASS}`}
                                />
                            </div>
                            <h2 className={`text-3xl font-extrabold text-gray-900`}>
                                {mvp[1].title}
                            </h2>
                        </div>
                        <p className="text-lg text-gray-700 pt-0">
                            {mvp[1].description}
                        </p>
                    </motion.div>

                </div>
            </section>

            <section className="py-8 bg-white">
                <div className="max-w-6xl mx-auto px-6 text-center">
                    <motion.p
                        className="text-sm font-semibold uppercase tracking-widest text-gray-500 mb-2"
                        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                    >
                        {founders.tag}
                    </motion.p>
                    <motion.h2
                        className="text-3xl md:text-4xl font-extrabold mb-2 text-gray-900"
                        variants={headerAnimate} initial="hidden" whileInView="visible" viewport={{ once: true }}
                    >
                        {founders.headline}
                    </motion.h2>

                    <motion.p
                        className="text-lg text-gray-500 mb-16 max-w-xl mx-auto"
                        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
                    >
                        {founders.subtext}
                    </motion.p>

                    <motion.div
                        className="grid md:grid-cols-2 gap-10 max-w-4xl mx-auto"
                        variants={containerVariants} initial="hidden"
                        whileInView="visible" viewport={{ once: true, amount: 0.2 }}
                    >
                        {founders.list.map((founder, index) => (
                            <FounderCard key={index} {...founder} />
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Innovation Card */}
            {/* <section className={`py-10 ${LIGHT_BG_CLASS}`}>
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <motion.p
                        className="text-sm font-semibold uppercase tracking-widest text-gray-500 mb-2"
                        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                    >
                        {innovators.tag}
                    </motion.p>
                    <motion.h2
                        className="text-3xl md:text-4xl font-extrabold mb-4 text-gray-900"
                        variants={headerAnimate} initial="hidden" whileInView="visible" viewport={{ once: true }}
                    >
                        {innovators.headline}
                    </motion.h2>

                    <motion.p
                        className="text-lg text-gray-500 mb-10 max-w-2xl mx-auto"
                        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
                    >
                        {innovators.subtext}
                    </motion.p>

                    <motion.div
                        className="grid sm:grid-cols-2 md:grid-cols-4 gap-8"
                        variants={containerVariants} initial="hidden"
                        whileInView="visible" viewport={{ once: true, amount: 0.1 }}
                    >
                        {innovators.list.map((innovator, index) => (
                            <InnovatorCard key={index} {...innovator} />
                        ))}
                    </motion.div>
                </div>
            </section> */}


            <section className="py-8 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
                    <motion.p
                        className="text-sm font-semibold uppercase tracking-widest text-gray-500 mb-2"
                        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                    >
                        {coreValues.tag}
                    </motion.p>
                    <motion.h2
                        className="text-4xl lg:text-5xl font-extrabold mb-4 text-gray-900"
                        variants={headerAnimate} initial="hidden" whileInView="visible" viewport={{ once: true }}
                    >
                        {coreValues.headline}
                    </motion.h2>

                    <motion.p
                        className="text-lg text-gray-500 mb-8 max-w-3xl mx-auto"
                        variants={headerAnimate} initial="hidden"
                        whileInView="visible" viewport={{ once: true }} transition={{ delay: 0.1 }}
                    >
                        {coreValues.subtext}
                    </motion.p>

                    <motion.div
                        className={`grid sm:grid-cols-2 lg:grid-cols-2 gap-8 max-w-4xl mx-auto`}
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        {coreValues.list.map((value, index) => (
                            <ValueCard key={index} {...value} />
                        ))}
                    </motion.div>
                </div>
            </section>
            <Footer />
        </div>
    );
};

export default AboutPage;